/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Box,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import Modal from './modal/modal';
import Typography from './typography/typography';
import Button from './button/button';
import ModalLoader from './loaders/modal-loader';
import { DynamicInput, DynamicSearchSelect, IModalComponentProps } from './form-control';

interface IFormValues {
    team: {
        name: string;
        department_id: string | null;
        team_lead_id: string | null;
        member_ids: string[] | null;
        description: string;
    };
}

interface ITeamAddModalProps extends IModalComponentProps {
    initialValues?: {
        department_id?: string | number | null;
    };
}

const teamValidationSchema = Yup.object().shape({
    team: Yup.object().shape({
        name: Yup.string().required('Team Name is required'),
        department_id: Yup.string().nullable(),
        team_lead_id: Yup.string().nullable().required('Team Lead is required'),
        member_ids: Yup.array().nullable(),
        description: Yup.string(),
    })
});

const getDefaultValues = (initialValues?: ITeamAddModalProps['initialValues']) => ({
    team: {
        name: '',
        department_id: initialValues?.department_id ?? null,
        team_lead_id: null,
        member_ids: null,
        description: '',
    }
});

export const TeamAddModal: React.FC<ITeamAddModalProps> = ({
    isOpen,
    setIsOpen,
    onSave,
    isLoading,
    modalProps,
    initialValues,
}) => {
    const { t } = useTranslation();

    const { control, handleSubmit, reset, watch } = useForm<IFormValues>({
        mode: 'all',
        resolver: yupResolver(teamValidationSchema),
        defaultValues: getDefaultValues(initialValues)
    });

    const selectedDepartmentId = watch('team.department_id');
    const selectedTeamLeadId = watch('team.team_lead_id');

    useEffect(() => {
        if (isOpen) {
            reset(getDefaultValues(initialValues));
        }
    }, [isOpen]);

    const handleModalClose = () => {
        reset(getDefaultValues(initialValues));
        setIsOpen();
    };

    const onSubmit = async (values: IFormValues) => {
        await onSave(values);
        handleModalClose();
    };

    const leadFilter = useMemo(() => ({
        '&department_id.eq': selectedDepartmentId || 0
    }), [selectedDepartmentId]);

    const membersFilter = useMemo(() => ({
        '&department_id.eq': selectedDepartmentId || 0,
        ...(selectedTeamLeadId ? { '&id.ne': selectedTeamLeadId } : {})
    }), [selectedDepartmentId, selectedTeamLeadId]);

    return (
        <Modal
            open={isOpen}
            onClose={handleModalClose}
            maxWidth='sm'
            fullWidth
            {...modalProps}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                {isLoading && <ModalLoader />}
                <DialogTitle className='brandAddModal--DialogTitle'>
                    <Typography type='s3' color='inherit' weight='medium'>
                        {t('hrms.organization_structure.teamModal.title', 'Add Team')}
                    </Typography>
                    <IconButton onClick={handleModalClose}>
                        <Close fontSize='small' />
                    </IconButton>
                </DialogTitle>
                <DialogContent className='brandAddModal--DialogContents'>
                    <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
                        {/* Team Name - Required */}
                        <Grid item sm={12}>
                            <DynamicInput
                                type='text'
                                formControl={control}
                                label={t('hrms.organization_structure.teamModal.name_label', 'Team Name')}
                                name='name'
                                fieldArrayName='team'
                                placeholder={t('hrms.organization_structure.teamModal.name_placeholder', 'Enter Team Name')}
                                required
                            />
                        </Grid>

                        {/* Department - Optional */}
                        <Grid item sm={6}>
                            <DynamicSearchSelect
                                label={t('hrms.organization_structure.teamModal.department_label', 'Department')}
                                name='department_id'
                                placeholder={t('hrms.organization_structure.teamModal.department_placeholder', 'Select Department')}
                                fieldArrayName='team'
                                formControl={control as any}
                                apiType='department'
                                required={false}
                                options={[]}
                            />
                        </Grid>

                        {/* Team Lead - Required */}
                        <Grid item sm={6}>
                            <DynamicSearchSelect
                                label={t('hrms.organization_structure.teamModal.team_lead_label', 'Team Lead')}
                                name='team_lead_id'
                                placeholder={t('hrms.organization_structure.teamModal.team_lead_placeholder', 'Select Team Lead')}
                                fieldArrayName='team'
                                formControl={control as any}
                                apiType='employees'
                                required
                                options={[]}
                                customeFilter={leadFilter}
                            />
                        </Grid>

                        {/* Members - Optional, multi-select */}
                        <Grid item sm={12}>
                            <DynamicSearchSelect
                                label={t('hrms.organization_structure.teamModal.members_label', 'Members')}
                                name='member_ids'
                                placeholder={t('hrms.organization_structure.teamModal.members_placeholder', 'Select Members')}
                                fieldArrayName='team'
                                formControl={control as any}
                                apiType='employees'
                                required={false}
                                options={[]}
                                is_multiselect
                                customeFilter={membersFilter}
                            />
                        </Grid>

                        {/* Description - Optional, full width */}
                        <Grid item sm={12}>
                            <DynamicInput
                                type='textarea'
                                formControl={control}
                                label={t('hrms.organization_structure.teamModal.description_label', 'Description')}
                                name='description'
                                fieldArrayName='team'
                                placeholder={t('hrms.organization_structure.teamModal.description_placeholder', 'Enter Description')}
                                is_multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className='brandAddModal--DialogAction'>
                    <span></span>
                    <Box display='flex' gap={1.5}>
                        <Button
                            className='brandAddModal--DialogAction--WhiteBtn'
                            onClick={handleModalClose}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button type='submit'>
                            {t('common.save')}
                        </Button>
                    </Box>
                </DialogActions>
            </form>
        </Modal>
    );
};
